import { BOOK_SLIDE } from 'artkive/components/sections/YouWillAlsoLoveCarousel'
import { FRAMED_MOSAIC } from 'artkive/constants/tracker/mainNavigation'
import { HELP_EMAIL } from 'artkive/utils/company'
import { MOSAIC_SIZES } from 'artkive/utils/constants'

import PileBefore from 'images/ecom/books/Books-Pile-Before.jpg'
import About1 from 'images/ecom/mosaics/Mosaic-Focus-1.png'
import About2 from 'images/ecom/mosaics/Mosaic-Focus-2.png'
import About3 from 'images/ecom/mosaics/Mosaic-Focus-3.png'
import Keepsake from 'images/ecom/mosaics/Mosaics-Keepsake.jpg'
import PileAfter from 'images/ecom/mosaics/Mosaics-Pile-After.jpg'
import RoomHallway from 'images/ecom/mosaics/Mosaics-Room-Hallway.jpg'
import RoomLivingRoom from 'images/ecom/mosaics/Mosaics-Room-LivingRoom.jpg'
import RoomPlayroom from 'images/ecom/mosaics/Mosaics-Room-Playroom.jpg'
import WhoItsForGrandparents from 'images/ecom/mosaics/Mosaics-WhoItsFor-Grandparents.jpg'
import WhoItsForMilestones from 'images/ecom/mosaics/Mosaics-WhoItsFor-Milestones.jpg'
import WhatsInThisBox from 'images/ecom/process/Process-WhatsInThisBox.jpg'
import Process from 'images/ecom/process/Process1.png'
import Process2 from 'images/ecom/process/Process2.jpg'
import Process4 from 'images/ecom/process/Process4.png'

export const FAQS = [
  {
    id: 1,
    title: 'Are there different color options for the mosaic frame?',
    body: 'The mosaic frame is available in white or black wood, both with white matting.',
  },
  {
    id: 2,
    title: 'What size are the framed mosaics?',
    body: 'The large mosaic is 26"x26" with a 24"x24" gallery print; the small is 14"x14" with a 12"x12" print.',
  },
  {
    id: 3,
    title: 'Can I put my child’s name in my framed mosaic?',
    body: 'Yes, we can add your child’s name to the bottom right corner at your request.',
  },
  {
    id: 4,
    title: 'How long does the mosaic process take?',
    body: `About 4-5 weeks to proof once we receive your art, then 1-2 weeks to print, frame, and ship
after approval. Ask us about rushing your mosaic order at
[${HELP_EMAIL}](mailto:${HELP_EMAIL}) (please note there may be additional fees involved).`,
  },
  {
    id: 5,
    title: 'Is this a keepsake or just wall art?',
    body: 'Both. It’s built to be looked at daily and to last for decades, the way a keepsake should.',
  },
  {
    id: 6,
    title: 'Will the frame and matting last, or fade over time?',
    body: 'The solid wood frame and archival matting are built for permanent display, not the fading you’d expect from a poster or print.',
  },
  {
    id: 7,
    title: 'Is this a good gift for grandparents?',
    body: 'Yes, it’s one of our most-gifted formats for grandparents who want a statement piece for their wall, not another small frame.',
  },
]

export const mosaicsProductBullets = [
  { text: 'Archival quality matting and solid wood framing built to hang for decades, not seasons' },
  {
    text:
      `Showcases ${MOSAIC_SIZES.CLASSIC} or ${MOSAIC_SIZES.SMALL} pieces at once, ` +
      'so a whole year of art gets one home, not a drawer',
  },
  { text: 'Professional photography and editing on every piece, exactly as it is on the Kids Art Book' },
  { text: 'Free digital copy included, so nothing is lost if the frame is ever damaged' },
  { text: 'Available in two sizes, so it fits a gallery wall or a single statement spot' },
]

export const rooms = {
  intro: [
    `A framed mosaic takes a collection of your child's artwork, up to ${MOSAIC_SIZES.CLASSIC} pieces, and turns it ` +
      `into a single gallery-style piece for the wall, rather than ${MOSAIC_SIZES.CLASSIC} separate drawings taped up ` +
      'or stuffed in a drawer. Every piece is professionally photographed and matted into a solid wood frame built ' +
      'for permanent display, not seasonal decor.',
    "It's a popular choice for the room where everyone gathers - a way to display a whole year of creativity at once instead of filing it away.",
  ],
  cards: [
    {
      title: 'Living Rooms',
      image: RoomLivingRoom,
      alt: 'Two kids lying on a couch beneath two framed mosaics of their artwork',
    },
    {
      title: 'Hallways',
      image: RoomHallway,
      alt: 'A dad and two kids pointing at framed mosaics hanging in a hallway',
    },
    {
      title: 'Playrooms',
      image: RoomPlayroom,
      alt: 'A girl reading in a chair under a framed mosaic in a playroom',
    },
  ],
  outro:
    "Parents use it to finally do something with the pile without picking favorites, since every piece included gets its place in the mosaic. Grandparents and new homeowners often choose it as a statement piece, and it's a natural gift at the end of a school year or a big move, when a blank wall needs one meaningful thing to hang first.",
}

export const beforeAfter = {
  title: "Before it's on the wall, | it's on the fridge.",
  body: "Twenty-five drawings taped up is chaos. One frame is a gallery. You don't have to choose your favorite piece and let the rest go in the recycling; the mosaic keeps the whole collection together, in the one place in the house everyone actually looks.",
  before: {
    image: PileBefore,
    alt: 'A child sorting through a box piled with drawings and paintings',
  },
  after: {
    image: PileAfter,
    alt: "A framed mosaic of kids' art hanging above a console table",
  },
  cta: {
    label: 'See How It Works',
    href: '#how-it-works',
    trackerEvent: FRAMED_MOSAIC.SEE_HOW_IT_WORKS_CLICK,
    section: 'mosaics_before_after',
  },
}

export const keepsakeQuote = {
  title: 'A Piece Worth | Passing Down, | Not Just Putting Up.',
  titleWidth: 511,
  body: "A poster fades and comes down eventually. This is framed to hang for decades: solid wood, archival matting, built for the wall you'll still have when your kid brings their own kids to visit. It's not decor you'll swap out next season, it's the one thing on the wall nobody wants you to take down. Combine two kids' art into one frame: one mosaic can hold artwork from more than one child, so siblings share the wall, too.",
  image: {
    image: Keepsake,
    alt: 'Two kids looking up at two framed mosaics of their artwork',
  },
  quote: {
    text: "I surprised her with a mosaic print that is perfect for her room. We couldn't be happier with the finished product.",
    author: 'Judy H.',
  },
  cta: { label: 'Get My Box', section: 'mosaics_keepsake' },
}

export const galleryWallFeatures = [
  {
    title: 'Solid wood gallery frame, | worthy of any room',
    body: 'Not a poster frame, a real piece of furniture-grade framing.',
    image: About1,
    alt: 'The corner of a framed mosaic showing the solid wood frame',
  },
  {
    title: 'Professional matting | makes the artwork shine',
    body: "Museum-style presentation for a kid's drawing.",
    image: About2,
    alt: 'A close-up of matted artwork inside a framed mosaic',
  },
  {
    title: 'A brilliant way | to showcase | themed art, too',
    body: "A school project series, a summer of beach drawings, one artist's whole year.",
    image: About3,
    alt: 'A framed mosaic made from a themed set of drawings',
  },
]

export const mosaicHowItWorks = [
  {
    image: WhatsInThisBox,
    title: 'Order | Your Box',
    body: 'Same box as the Kids Art Book; you can send art for both in one shipment.',
  },
  {
    image: Process,
    title: 'Choose | Your Favorites',
    body: "Pick the pieces you want on permanent display; we'll help with layout.",
  },
  {
    image: Process2,
    title: 'We Photograph | & Design',
    narrowTitle: true,
    body: 'Every piece professionally shot and arranged into your mosaic proof.',
  },
  {
    image: Process4,
    title: 'Approve | & Frame',
    body: "Confirm the layout, then it's custom-framed and shipped ready to hang.",
  },
]

export const whoItsFor = {
  title: "Who It's For",
  subtitle: 'Made for the wall everyone stops at.',
  cta: { label: 'Get My Box', section: 'mosaics_who_its_for' },
}

export const recipientGroups = [
  {
    title: 'Grandparents',
    body: 'A statement piece for the living room wall, not another small frame on a side table.',
    image: WhoItsForGrandparents,
    alt: 'Grandparents in front of a framed mosaic in their living room',
    size: 'large',
  },
  {
    title: 'Milestones',
    body: 'End of a school year, a big art phase, a graduation from the crayon years to the marker years — a natural gift moment.',
    image: WhoItsForMilestones,
    alt: 'A framed mosaic given as an end-of-year gift',
    size: 'large',
  },
]

export const testimonialUser = {
  name: 'Tara M.',
}

export const imageBgPosition = {
  xs: '50% 50%',
  sm: '50% 70%',
}

export const slides = [BOOK_SLIDE]

export const asterisks = ['*Price doesn’t include Artkive Box']
